// src/app/pages/ProfesionalesKine.tsx
import { useState } from "react"
import { Link } from "react-router"
import { LayoutDashboard, Users, Dumbbell, Calendar, MessageCircle, Settings, Search, UserPlus } from "lucide-react"
import { Avatar } from "../components/Avatar"
import { DarkModeToggle } from "../components/DarkModeToggle"
import logoGonixLight from "@/assets/logo-gonix-light.png"
import logoGonixDark from "@/assets/logo-gonix-dark.png"
import { RoleToggle } from "../components/RoleToggle"
import { Button } from "@/app/components/ui/button"
import { Input } from "@/app/components/ui/input"
import { Label } from "@/app/components/ui/label"
import { Textarea } from "@/app/components/ui/textarea"
import { Badge } from "@/app/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/components/ui/select"

// NOTA: AlertDialog de shadcn requiere @radix-ui/react-alert-dialog
// que aún no está instalado. Post mini PC corremos:
//   pnpm install @radix-ui/react-alert-dialog
// y volvemos al AlertDialog (mejor UX). Por ahora usamos window.confirm.

// ─── Types ────────────────────────────────────────────────────────────────────

type Rol = "kinesiologo" | "admin" | "readonly"
type EstadoProfesional = "activo" | "inactivo"
type Especialidad =
  | "general"
  | "deportiva"
  | "neurologica"
  | "traumatologica"
  | "pediatrica"

interface Profesional {
  id: string
  nombre: string
  matricula: string
  email: string
  telefono: string
  especialidad: Especialidad
  rol: Rol
  estado: EstadoProfesional
  bio: string
  pacientesCount: number
}

// ─── Mock data ────────────────────────────────────────────────────────────────
// TODO(post-MVP): reemplazar por query a Supabase (tabla kinesiologos + profiles).

const MOCK_PROFESIONALES: Profesional[] = [
  {
    id: "p1",
    nombre: "Dr. Alejandro Martínez",
    matricula: "12.456",
    email: "a.martinez@gonix.com",
    telefono: "+54 11 4567-8901",
    especialidad: "traumatologica",
    rol: "kinesiologo",
    estado: "activo",
    bio: "Especializado en rehabilitación traumatológica y deportiva con 12 años de experiencia.",
    pacientesCount: 5,
  },
  {
    id: "p2",
    nombre: "Dra. Analía López",
    matricula: "9.321",
    email: "a.lopez@gonix.com",
    telefono: "+54 11 3456-7890",
    especialidad: "deportiva",
    rol: "kinesiologo",
    estado: "activo",
    bio: "Enfocada en kinesiología deportiva y prevención de lesiones.",
    pacientesCount: 3,
  },
  {
    id: "p3",
    nombre: "Dr. Roberto Fernández",
    matricula: "18.002",
    email: "r.fernandez@gonix.com",
    telefono: "",
    especialidad: "general",
    rol: "kinesiologo",
    estado: "inactivo",
    bio: "",
    pacientesCount: 0,
  },
]

const EMPTY_FORM: Omit<Profesional, "id" | "pacientesCount"> = {
  nombre: "",
  matricula: "",
  email: "",
  telefono: "",
  especialidad: "general",
  rol: "kinesiologo",
  estado: "activo",
  bio: "",
}

const ESPECIALIDAD_LABEL: Record<Especialidad, string> = {
  general: "Kinesiología general",
  deportiva: "Deportiva",
  neurologica: "Neurológica",
  traumatologica: "Traumatológica",
  pediatrica: "Pediátrica",
}

const ROL_LABEL: Record<Rol, string> = {
  kinesiologo: "Kinesiólogo",
  admin: "Administrador",
  readonly: "Solo lectura",
}

function initials(nombre: string) {
  return nombre
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ProfesionalesKine() {
  const [profesionales, setProfesionales] = useState<Profesional[]>(MOCK_PROFESIONALES)
  const [selectedId, setSelectedId] = useState<string | null>(MOCK_PROFESIONALES[0].id)
  const [isNew, setIsNew] = useState(false)
  const [form, setForm] = useState<Omit<Profesional, "id" | "pacientesCount">>(EMPTY_FORM)
  const [search, setSearch] = useState("")

  const selected = profesionales.find((p) => p.id === selectedId) ?? null

  const filtered = profesionales.filter((p) =>
    p.nombre.toLowerCase().includes(search.toLowerCase())
  )

  const handleSelect = (p: Profesional) => {
    setIsNew(false)
    setSelectedId(p.id)
    const { id, pacientesCount, ...rest } = p
    setForm(rest)
  }

  const handleNew = () => {
    setIsNew(true)
    setSelectedId(null)
    setForm(EMPTY_FORM)
  }

  const handleSave = () => {
    if (isNew) {
      const newP: Profesional = {
        ...form,
        id: crypto.randomUUID(),
        pacientesCount: 0,
      }
      setProfesionales((prev) => [...prev, newP])
      setIsNew(false)
      setSelectedId(newP.id)
    } else if (selectedId) {
      setProfesionales((prev) =>
        prev.map((p) => (p.id === selectedId ? { ...p, ...form } : p))
      )
    }
  }

  const handleDeactivate = () => {
    if (!selectedId || !selected) return
    const msg =
      selected.estado === "activo"
        ? "¿Desactivar profesional? Perderá acceso a la plataforma. Sus pacientes no serán eliminados."
        : "¿Reactivar profesional? Recuperará acceso a la plataforma."
    if (!window.confirm(msg)) return
    setProfesionales((prev) =>
      prev.map((p) =>
        p.id === selectedId
          ? { ...p, estado: p.estado === "activo" ? "inactivo" : "activo" }
          : p
      )
    )
    setForm((f) => ({ ...f, estado: f.estado === "activo" ? "inactivo" : "activo" }))
  }

  const set =
    <K extends keyof typeof form>(key: K) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }))

  return (
    <div className="min-h-screen bg-background transition-colors duration-200">
      {/* Header */}
      <div className="bg-card border-b border-border shadow-sm">
        <div className="max-w-[1400px] mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={logoGonixLight}
              alt="Gonix"
              className="block dark:hidden h-10 w-auto object-contain"
            />
            <img
              src={logoGonixDark}
              alt="Gonix"
              className="hidden dark:block h-10 w-auto object-contain"
            />
          </div>
          <div className="flex items-center gap-3 md:gap-4">
            <RoleToggle />
            <DarkModeToggle />
            <span className="text-sm">Dr. Martínez</span>
            <Avatar name="Dr. Martínez" colorIndex={0} />
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div className="w-56 bg-sidebar border-r border-sidebar-border min-h-[calc(100vh-73px)] p-4">
          <div className="space-y-1">
            <Link to="/" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sidebar-foreground hover:bg-sidebar-accent transition-all duration-200 ease-out">
              <Users className="w-5 h-5" strokeWidth={1.5} />
              <span>Pacientes</span>
            </Link>
            <Link to="/rutinas" className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-sidebar-accent text-sidebar-foreground transition-all duration-200 ease-out">
              <LayoutDashboard className="w-5 h-5" strokeWidth={1.5} />
              <span>Rutinas</span>
            </Link>
            <Link to="/biblioteca" className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-sidebar-accent text-sidebar-foreground transition-all duration-200 ease-out">
              <Dumbbell className="w-5 h-5" strokeWidth={1.5} />
              <span>Biblioteca ejercicios</span>
            </Link>
            <Link to="/calendario" className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-sidebar-accent text-sidebar-foreground transition-all duration-200 ease-out">
              <Calendar className="w-5 h-5" strokeWidth={1.5} />
              <span>Calendario</span>
            </Link>
            <Link to="/mensajes-kine" className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-sidebar-accent text-sidebar-foreground transition-all duration-200 ease-out">
              <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
              <span>Mensajes</span>
            </Link>
            <Link to="/configuracion" className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-sidebar-accent text-sidebar-foreground transition-all duration-200 ease-out">
              <Settings className="w-5 h-5" strokeWidth={1.5} />
              <span>Configuración</span>
            </Link>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-8">
          <div className="max-w-[1400px]">
            {/* Page header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold">Profesionales</h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {profesionales.filter((p) => p.estado === "activo").length} profesionales activos
                </p>
              </div>
              <Button onClick={handleNew}>
                <UserPlus className="h-4 w-4 mr-2" />
                Nuevo profesional
              </Button>
            </div>

            {/* Master-detail */}
            <div className="flex rounded-xl border border-border bg-card overflow-hidden min-h-[600px]">
              {/* List */}
              <div className="w-72 shrink-0 border-r border-border flex flex-col">
                <div className="p-3 border-b border-border">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      className="pl-8"
                      placeholder="Buscar profesional..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {filtered.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => handleSelect(p)}
                      className={`w-full flex items-center gap-3 px-4 py-3 border-b border-border text-left transition-colors hover:bg-muted/40 ${
                        selectedId === p.id && !isNew
                          ? "bg-primary/10 border-l-2 border-l-primary pl-[14px]"
                          : ""
                      }`}
                    >
                      <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center text-xs font-bold shrink-0">
                        {initials(p.nombre)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate">{p.nombre}</p>
                        <p className="text-xs text-muted-foreground">
                          Mat. {p.matricula} · {p.pacientesCount} pac.
                        </p>
                      </div>
                      <Badge variant={p.estado === "activo" ? "default" : "secondary"} className="text-[10px]">
                        {p.estado === "activo" ? "Activo" : "Inactivo"}
                      </Badge>
                    </button>
                  ))}
                </div>
              </div>

              {/* Form */}
              <div className="flex-1 overflow-y-auto p-6">
                {!selectedId && !isNew ? (
                  <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                    Seleccioná un profesional para editarlo
                  </div>
                ) : (
                  <div className="max-w-xl space-y-4">
                    <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                      {isNew ? "Nuevo profesional" : "Perfil del profesional"}
                    </p>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5 col-span-2">
                        <Label>Nombre completo</Label>
                        <Input value={form.nombre} onChange={set("nombre")} placeholder="Dr. / Dra. ..." />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Matrícula profesional</Label>
                        <Input value={form.matricula} onChange={set("matricula")} placeholder="N° matrícula" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Teléfono</Label>
                        <Input value={form.telefono} onChange={set("telefono")} placeholder="+54 11..." />
                      </div>
                      <div className="space-y-1.5 col-span-2">
                        <Label>Email</Label>
                        <Input type="email" value={form.email} onChange={set("email")} placeholder="nombre@gonix.com" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Especialidad</Label>
                        <Select
                          value={form.especialidad}
                          onValueChange={(v) => setForm((f) => ({ ...f, especialidad: v as Especialidad }))}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {(Object.entries(ESPECIALIDAD_LABEL) as [Especialidad, string][]).map(([v, l]) => (
                              <SelectItem key={v} value={v}>{l}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label>Estado</Label>
                        <Select
                          value={form.estado}
                          onValueChange={(v) => setForm((f) => ({ ...f, estado: v as EstadoProfesional }))}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="activo">Activo</SelectItem>
                            <SelectItem value="inactivo">Inactivo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label>Bio / descripción</Label>
                      <Textarea
                        value={form.bio}
                        onChange={set("bio")}
                        placeholder="Especialización, años de experiencia, enfoque terapéutico..."
                        rows={3}
                      />
                    </div>

                    <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground pt-2">
                      Accesos y permisos
                    </p>
                    <div className="space-y-1.5">
                      <Label>Rol en la plataforma</Label>
                      <Select
                        value={form.rol}
                        onValueChange={(v) => setForm((f) => ({ ...f, rol: v as Rol }))}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {(Object.entries(ROL_LABEL) as [Rol, string][]).map(([v, l]) => (
                            <SelectItem key={v} value={v}>{l}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {isNew && (
                      <p className="text-xs text-muted-foreground">
                        💡 Al crear el profesional se enviará un email con sus credenciales de acceso.
                      </p>
                    )}

                    <div className="flex gap-2 justify-end pt-2">
                      {!isNew && selected && (
                        <Button variant="outline" onClick={handleDeactivate}>
                          {selected.estado === "activo" ? "Desactivar cuenta" : "Reactivar cuenta"}
                        </Button>
                      )}
                      <Button onClick={handleSave}>
                        {isNew ? "Crear profesional" : "Guardar cambios"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
